import BigNumber from 'bignumber.js'
import type { Maybe, BaseCoinPrice } from '@/types'

type AmountConstructorParams = {
  amount: string | BigNumber
  gweiAmount?: never
  ethAmount?: never
  decimal: number
}
type EthAmountConstructorParams = {
  amount?: never
  gweiAmount?: never
  ethAmount: string | BigNumber
  decimal: number
}
type GweiAmountConstructorParams = {
  amount?: never
  gweiAmount: string | BigNumber
  ethAmount?: never
  decimal: number
}

function isAmountConstructorParams(obj: any): obj is AmountConstructorParams {
  return 'amount' in obj
}
function isGweiAmountConstructorParams(
  obj: any,
): obj is GweiAmountConstructorParams {
  return 'gweiAmount' in obj
}

/**
 * Coin 의 수량을 나타내는 class
 * amount 는 해당 코인의 decimal 만큼 shift 된 BigNumber로 소수점이 없는 단위의 코인이다. (ethereum 의 경우 wei)
 * ethValue 는 amount 를 decimal 만큼 shift 한 BigNumber로 소수점이 있는 단위의 코인이다. (ethereum 의 경우 eth) 나중에 이름을 coinValue 등으로 변경 필요할듯
 */
export class CoinAmount {
  amount: BigNumber
  decimal: number

  constructor(
    params:
      | AmountConstructorParams
      | EthAmountConstructorParams
      | GweiAmountConstructorParams,
  ) {
    this.decimal = params.decimal

    if (isAmountConstructorParams(params)) {
      this.amount =
        typeof params.amount === 'string'
          ? new BigNumber(params.amount)
          : params.amount
    } else if (isGweiAmountConstructorParams(params)) {
      this.amount = (
        typeof params.gweiAmount === 'string'
          ? new BigNumber(params.gweiAmount)
          : params.gweiAmount
      ).shiftedBy(9)
    } else {
      // ethAmount → wei 변환 (dcent getMultiplePrice 방식)
      const ethAmountStr =
        typeof params.ethAmount === 'string'
          ? params.ethAmount
          : params.ethAmount.toFixed()
      this.amount = new BigNumber(
        new BigNumber(ethAmountStr)
          .multipliedBy(new BigNumber(10).pow(params.decimal))
          .toFixed(0, BigNumber.ROUND_FLOOR),
      )
    }
  }

  get ethValue() {
    return this.amount.shiftedBy(-this.decimal)
  }

  get weiValue() {
    return this.amount
  }

  get gweiValue() {
    return this.amount.shiftedBy(-9)
  }

  set gweiValue(value: BigNumber) {
    this.amount = value.shiftedBy(9)
  }

  calculatePrice(price: Maybe<BaseCoinPrice>) {
    if (!price) return new BigNumber(0)
    // price.price가 없거나 NaN이면 0 반환
    if (!price.price || isNaN(Number(price.price))) return new BigNumber(0)
    return this.ethValue.multipliedBy(price.price)
  }
}
